import React, { FC, useState, useEffect, ChangeEvent, FormEvent } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { setHeaders, url } from '../../api'; // Assuming 'url' is your API base URL
import { useSelector } from 'react-redux';
import { RootState } from 'src/store/reducers/types';

// --- Interfaces (as provided by user for data structure, adapted for form if needed) ---
export interface Tweet {
    tweetId?: string; // Optional for new tweets if backend generates ID
    tweetUrl?: string;
    datePosted?: string; // Full ISO 8601 UTC string
    // tweetReplys?: Tweet[]; // Keep as JSON for simplicity, or omit if not editable here
}

export interface GenerationStatusData {
    status: 'pending' | 'generating' | 'completed' | 'failed' | 'no_art' | 'error' | 'birthday_priority' | 'started' | 'not_started';
    message?: string;
    state?: 'initialized' | 'generated_prompt' | 'generated_image' | 'posted_tweet' | 'posted_tg' | 'done' | 'error';
    startedAt?: string; // Full ISO 8601 UTC string
    completedAt?: string; // Full ISO 8601 UTC string
    error?: string;
}

// Main data structure from backend for an AOTD entry
export interface AOTDServerData {
    _id: string;
    url?: string;
    createdBy: string;
    authorQuote?: string;
    date: string | Date; // Can be string or Date from server
    prompt?: string;
    views?: {
        verified: number;
        total: number;
    };
    title?: string;
    quoteHelp?: number;
    tweet?: { tweetId?: string; tweetUrl?: string; datePosted?: string | Date; } | null;
    emojis?: string[];
    generationStatus?: {
        status: GenerationStatusData['status'];
        message?: string;
        state?: GenerationStatusData['state'];
        startedAt?: string | Date;
        completedAt?: string | Date;
        error?: string;
    };
    __v?: number;
}

// Data structure for the editable form state
export interface AOTDFormData extends Omit<AOTDServerData, 'date' | 'tweet' | 'generationStatus' | '_id' | '__v'> {
    _id?: string;
    __v?: number;
    date?: string; // Full ISO 8601 UTC string
    tweet?: Partial<Tweet>;
    generationStatus?: Partial<GenerationStatusData>;
    views?: {
        verified: number;
        total: number;
    };
}

// --- Styled Components ---
const AdminPanelContainer = styled.div`
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 20px;
    margin-top: 20px;
    max-width: 900px;
    width: 100%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const PanelHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    margin-bottom: 10px;

    h2 {
        margin: 0;
        color: #343a40;
    }

    span {
        font-size: 1.5rem;
    }
`;

const PanelContent = styled.div<{ isOpen: boolean }>`
    display: ${(props) => (props.isOpen ? 'block' : 'none')};
`;

const FormGroup = styled.div`
    margin-bottom: 15px;

    label {
        display: block;
        margin-bottom: 5px;
        font-weight: bold;
        color: #495057;
    }

    input[type="text"],
    input[type="number"],
    /* input[type="datetime-local"], // No longer using datetime-local */
    select,
    textarea {
        width: 100%;
        padding: 8px;
        border: 1px solid #ced4da;
        border-radius: 4px;
        box-sizing: border-box;
        font-size: 0.9rem;
    }

    textarea {
        min-height: 80px; 
        font-family: monospace;
        resize: vertical; 
    }
    
    input[readonly] {
        background-color: #e9ecef;
        cursor: not-allowed;
    }
`;

// const DateDisplay = styled.small` ... `; // Removed as per simplification

const SubGroup = styled.div`
    border-left: 3px solid #007bff;
    padding-left: 15px;
    margin-top: 10px;
    margin-bottom: 15px;

    h5 {
        margin-top: 0;
        margin-bottom: 10px;
        color: #007bff;
    }
`;

const ButtonGroup = styled.div`
    margin-top: 20px;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;

    button {
        padding: 10px 15px;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.9rem;
        transition: background-color 0.2s ease;
    }
`;

const PrimaryButton = styled.button`
    background-color: #007bff;
    color: white;
    &:hover {
        background-color: #0056b3;
    }
`;

const DangerButton = styled.button`
    background-color: #dc3545;
    color: white;
    &:hover {
        background-color: #c82333;
    }
`;

const InfoButton = styled.button`
    background-color: #17a2b8;
    color: white;
    &:hover {
        background-color: #117a8b;
    }
`;

const FetchControls = styled.div`
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
    align-items: center;

    input {
        flex-grow: 1;
    }
`;

const LoadingErrorDisplay = styled.p`
    color: ${(props: { color?: string }) => (props.color === 'error' ? '#dc3545' : '#007bff')};
    font-style: italic;
`;

function checkRole(str: string, searchStr: string) {
    try {
        var arr = str.split(",");
    } catch {
        return false;
    }
    for (var i = 0; i < arr.length; i++) {
        if (arr[i].trim() === searchStr) {
            return true;
        }
    }
    return false;
}

// Helper to convert Date object or valid date string to ISO UTC string
// If input is already a string, it's assumed to be in correct ISO format or will be validated by backend.
const ensureISOString = (dateValue?: string | Date): string | undefined => {
    if (!dateValue) return undefined;
    if (typeof dateValue === 'string') {
        // Basic check if it resembles an ISO string, otherwise attempt conversion.
        // This is a simplification; robust validation might be needed if various string formats are expected.
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(dateValue)) {
            return dateValue;
        }
        try {
            return new Date(dateValue).toISOString();
        } catch (e) {
            // If parsing fails, it might be an invalid date string. Return undefined or handle as error.
            console.warn("Could not parse date string to ISO:", dateValue);
            return undefined;
        }
    }
    // It's a Date object
    try {
        return dateValue.toISOString();
    } catch (e) {
        console.warn("Could not convert Date object to ISO string:", dateValue);
        return undefined;
    }
};

const ArtOfTheDayAdminPanel: FC = () => {
    const [aotdData, setAotdData] = useState<AOTDServerData | null>(null); // Original server data
    const [editableData, setEditableData] = useState<Partial<AOTDFormData>>({}); // Form state, dates as ISO strings
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [fetchId, setFetchId] = useState<string>('');
    const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);

    const user = useSelector((state: RootState) => state.auth);
    const API_PREFIX = `${url}/api/art-of-the-day/admin`;

    const fetchData = async (id?: string) => {
        if (!checkRole(user.role || '', 'admin')) {
            setErrorMsg("You do not have permission to view this panel.");
            return;
        }
        setIsLoading(true);
        setErrorMsg(null);
        setAotdData(null);
        setEditableData({});

        const endpoint = id ? `${API_PREFIX}/${id}` : `${API_PREFIX}/today`;

        try {
            const response = await axios.get<AOTDServerData>(endpoint, setHeaders());
            const serverData = response.data;
            setAotdData(serverData);

            const formData: Partial<AOTDFormData> = {
                ...serverData,
                _id: serverData._id,
                __v: serverData.__v,
                date: ensureISOString(serverData.date),
                tweet: serverData.tweet ? {
                    ...serverData.tweet,
                    datePosted: ensureISOString(serverData.tweet.datePosted)
                } : undefined,
                generationStatus: serverData.generationStatus ? {
                    ...serverData.generationStatus,
                    startedAt: ensureISOString(serverData.generationStatus.startedAt),
                    completedAt: ensureISOString(serverData.generationStatus.completedAt),
                } : undefined,
            };
            setEditableData(formData);
        } catch (err: any) {
            console.error("Error fetching AOTD data:", err);
            const message = err.response?.data?.message || "Failed to fetch AOTD data.";
            setErrorMsg(message);
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (checkRole(user.role || '', 'admin')) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.role]);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        setEditableData(prev => {
            let processedValue: any = value;
            if (type === 'number') {
                processedValue = value === '' ? undefined : parseFloat(value);
            }
            return { ...prev, [name]: processedValue };
        });
    };

    const handleNestedInputChange = (
        objectKey: 'tweet' | 'generationStatus' | 'views',
        fieldKey: string,
        value: string | number | boolean,
    ) => {
        setEditableData(prev => {
            const currentObject = prev[objectKey] || {};
            return {
                ...prev,
                [objectKey]: {
                    ...(currentObject as object),
                    [fieldKey]: value,
                }
            };
        });
    };

    const handleEmojisChange = (value: string) => {
        setEditableData(prev => ({ ...prev, emojis: value.split(',').map(e => e.trim()).filter(e => e) }));
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!aotdData?._id || !checkRole(user.role || '', 'admin')) {
            toast.error("No AOTD loaded or insufficient permissions.");
            return;
        }
        setIsLoading(true);
        setErrorMsg(null);

        const submissionPayload: Partial<AOTDServerData> = {};
        const deepCompare = (obj1: any, obj2: any): boolean => {
            return JSON.stringify(obj1) === JSON.stringify(obj2);
        };

        // Date (main) - compare string against string
        if (editableData.date !== undefined && editableData.date !== ensureISOString(aotdData.date)) {
            submissionPayload.date = editableData.date;
        }

        const simpleFields: Array<keyof Pick<AOTDFormData, 'url' | 'createdBy' | 'authorQuote' | 'prompt' | 'title' | 'quoteHelp'>> =
            ['url', 'createdBy', 'authorQuote', 'prompt', 'title', 'quoteHelp'];
        simpleFields.forEach(key => {
            if (editableData[key] !== undefined && editableData[key] !== (aotdData as any)[key]) {
                (submissionPayload as any)[key] = editableData[key];
            }
        });

        // Handle views object separately
        if (editableData.views && !deepCompare(editableData.views, aotdData.views)) {
            submissionPayload.views = editableData.views;
        }

        if (editableData.emojis && !deepCompare(editableData.emojis, aotdData.emojis)) {
            submissionPayload.emojis = editableData.emojis;
        }

        const originalTweetForComparison = aotdData.tweet ? {
            ...aotdData.tweet,
            datePosted: ensureISOString(aotdData.tweet.datePosted)
        } : undefined;

        if (editableData.tweet && !deepCompare(editableData.tweet, originalTweetForComparison)) {
            submissionPayload.tweet = editableData.tweet as Tweet; // Dates in editableData.tweet are already strings
        }

        const originalGenStatusForComparison = aotdData.generationStatus ? {
            ...aotdData.generationStatus,
            startedAt: ensureISOString(aotdData.generationStatus.startedAt),
            completedAt: ensureISOString(aotdData.generationStatus.completedAt)
        } : undefined;

        if (editableData.generationStatus && !deepCompare(editableData.generationStatus, originalGenStatusForComparison)) {
            submissionPayload.generationStatus = editableData.generationStatus as GenerationStatusData; // Dates are already strings
        }

        if (Object.keys(submissionPayload).length === 0) {
            toast.info("No changes detected.");
            setIsLoading(false);
            return;
        }

        try {
            await axios.put(`${API_PREFIX}/${aotdData._id}`, submissionPayload, setHeaders());
            toast.success("AOTD updated successfully!");
            fetchData(aotdData._id);
        } catch (err: any) {
            console.error("Error updating AOTD:", err);
            const message = err.response?.data?.message || "Failed to update AOTD.";
            setErrorMsg(message);
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!aotdData?._id || !checkRole(user.role || '', 'admin')) {
            toast.error("No AOTD loaded or insufficient permissions.");
            return;
        }
        if (!window.confirm("Are you sure you want to delete this AOTD entry? This action cannot be undone.")) {
            return;
        }
        setIsLoading(true);
        setErrorMsg(null);
        try {
            await axios.delete(`${API_PREFIX}/${aotdData._id}`, setHeaders());
            toast.success("AOTD deleted successfully!");
            setAotdData(null);
            setEditableData({});
            setFetchId('');
        } catch (err: any) {
            console.error("Error deleting AOTD:", err);
            const message = err.response?.data?.message || "Failed to delete AOTD.";
            setErrorMsg(message);
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFetchById = () => {
        if (fetchId.trim()) {
            fetchData(fetchId.trim());
        } else {
            toast.warn("Please enter an AOTD ID to fetch.");
        }
    };

    if (!checkRole(user.role || '', 'admin')) {
        return <AdminPanelContainer><p>Admin access required.</p></AdminPanelContainer>;
    }

    const renderField = (key: keyof AOTDFormData, data: Partial<AOTDFormData>) => {
        const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        const value = data[key];

        if (key === '_id' || key === '__v') {
            return (
                <FormGroup key={key}>
                    <label htmlFor={key}>{label}</label>
                    <input type="text" id={key} name={key} value={String(value || '')} readOnly />
                </FormGroup>
            );
        }

        if (key === 'prompt') {
            return (
                <FormGroup key={key}>
                    <label htmlFor={key}>{label}</label>
                    <textarea id={key} name={key} value={String(value || '')} onChange={handleInputChange} rows={8} />
                </FormGroup>
            );
        }

        if (key === 'date') {
            return (
                <FormGroup key={key}>
                    <label htmlFor={key}>{label} (UTC ISO String)</label>
                    <input type="text" id={key} name={key} value={data.date || ''} onChange={handleInputChange} placeholder="YYYY-MM-DDTHH:mm:ss.sssZ" />
                </FormGroup>
            );
        }

        if (key === 'tweet' && data.tweet) {
            const tweetData = data.tweet;
            return (
                <SubGroup key={key}>
                    <h5>Tweet Details</h5>
                    <FormGroup>
                        <label htmlFor="tweetId">Tweet ID</label>
                        <input type="text" id="tweetId" name="tweetId" value={tweetData.tweetId || ''} onChange={(e) => handleNestedInputChange('tweet', 'tweetId', e.target.value)} />
                    </FormGroup>
                    <FormGroup>
                        <label htmlFor="tweetUrl">Tweet URL</label>
                        <input type="text" id="tweetUrl" name="tweetUrl" value={tweetData.tweetUrl || ''} onChange={(e) => handleNestedInputChange('tweet', 'tweetUrl', e.target.value)} />
                    </FormGroup>
                    <FormGroup>
                        <label htmlFor="tweetDatePosted">Date Posted (Tweet - UTC ISO String)</label>
                        <input type="text" id="tweetDatePosted" name="tweetDatePosted" value={tweetData.datePosted || ''} onChange={(e) => handleNestedInputChange('tweet', 'datePosted', e.target.value)} placeholder="YYYY-MM-DDTHH:mm:ss.sssZ" />
                    </FormGroup>
                </SubGroup>
            );
        }

        if (key === 'generationStatus' && data.generationStatus) {
            const gs = data.generationStatus;
            const statusOptions: GenerationStatusData['status'][] = ['pending', 'generating', 'completed', 'failed', 'no_art', 'error', 'birthday_priority', 'started', 'not_started'];
            const stateOptions: GenerationStatusData['state'][] = ['initialized', 'generated_prompt', 'generated_image', 'posted_tweet', 'posted_tg', 'done', 'error'];

            return (
                <SubGroup key={key}>
                    <h5>Generation Status</h5>
                    <FormGroup>
                        <label htmlFor="gs_status">Status</label>
                        <select id="gs_status" name="status" value={gs.status || 'not_started'} onChange={(e) => handleNestedInputChange('generationStatus', 'status', e.target.value)}>
                            {statusOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                    </FormGroup>
                    <FormGroup>
                        <label htmlFor="gs_message">Message</label>
                        <input type="text" id="gs_message" name="message" value={gs.message || ''} onChange={(e) => handleNestedInputChange('generationStatus', 'message', e.target.value)} />
                    </FormGroup>
                    <FormGroup>
                        <label htmlFor="gs_state">State</label>
                        <select id="gs_state" name="state" value={gs.state || ''} onChange={(e) => handleNestedInputChange('generationStatus', 'state', e.target.value)}>
                            <option value="">-- Select State --</option>
                            {stateOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                    </FormGroup>
                    <FormGroup>
                        <label htmlFor="gs_startedAt">Started At (UTC ISO String)</label>
                        <input type="text" id="gs_startedAt" name="startedAt" value={gs.startedAt || ''} onChange={(e) => handleNestedInputChange('generationStatus', 'startedAt', e.target.value)} placeholder="YYYY-MM-DDTHH:mm:ss.sssZ" />
                    </FormGroup>
                    <FormGroup>
                        <label htmlFor="gs_completedAt">Completed At (UTC ISO String)</label>
                        <input type="text" id="gs_completedAt" name="completedAt" value={gs.completedAt || ''} onChange={(e) => handleNestedInputChange('generationStatus', 'completedAt', e.target.value)} placeholder="YYYY-MM-DDTHH:mm:ss.sssZ" />
                    </FormGroup>
                    <FormGroup>
                        <label htmlFor="gs_error">Error Message</label>
                        <input type="text" id="gs_error" name="error" value={gs.error || ''} onChange={(e) => handleNestedInputChange('generationStatus', 'error', e.target.value)} />
                    </FormGroup>
                </SubGroup>
            );
        }

        if (key === 'emojis') {
            return (
                <FormGroup key={key}>
                    <label htmlFor={key}>{label} (comma-separated)</label>
                    <input type="text" id={key} name={key} value={(data.emojis || []).join(', ')} onChange={(e) => handleEmojisChange(e.target.value)} placeholder="e.g., ✨,🎨,🚀" />
                </FormGroup>
            );
        }

        if (key === 'quoteHelp') {
            return (
                <FormGroup key={key}>
                    <label htmlFor={key}>{label}</label>
                    <input type="number" id={key} name={key} value={value === undefined ? '' : String(value)} onChange={handleInputChange} />
                </FormGroup>
            );
        }

        if (key === 'views') {
            const views = value as { verified: number; total: number } | undefined;
            return (
                <SubGroup key={key}>
                    <h5>Views Stats</h5>
                    <FormGroup>
                        <label htmlFor="views_verified">Verified Views</label>
                        <input
                            type="number"
                            id="views_verified"
                            name="views_verified"
                            value={views?.verified === undefined ? '' : String(views.verified)}
                            onChange={(e) => handleNestedInputChange('views', 'verified', e.target.value === '' ? 0 : parseInt(e.target.value))}
                        />
                    </FormGroup>
                    <FormGroup>
                        <label htmlFor="views_total">Total Views</label>
                        <input
                            type="number"
                            id="views_total"
                            name="views_total"
                            value={views?.total === undefined ? '' : String(views.total)}
                            onChange={(e) => handleNestedInputChange('views', 'total', e.target.value === '' ? 0 : parseInt(e.target.value))}
                        />
                    </FormGroup>
                </SubGroup>
            );
        }

        if (typeof value === 'string' || value === undefined || value === null) {
            return (
                <FormGroup key={key}>
                    <label htmlFor={key}>{label}</label>
                    <input type="text" id={key} name={key} value={String(value || '')} onChange={handleInputChange} />
                </FormGroup>
            );
        }

        return (
            <FormGroup key={key}>
                <label htmlFor={key}>{label} (Unhandled Field Type)</label>
                <input type="text" value={String(value || '')} readOnly />
            </FormGroup>
        );
    };

    const fieldsToRender = aotdData
        ? (Object.keys(editableData) as Array<keyof AOTDFormData>)
            .filter(key => key !== '_id' && key !== '__v')
            .sort((a, b) => {
                const order: Array<keyof Omit<AOTDFormData, '_id' | '__v'>> = ['date', 'title', 'createdBy', 'authorQuote', 'prompt', 'url', 'views', 'quoteHelp', 'emojis', 'tweet', 'generationStatus'];
                return order.indexOf(a as any) - order.indexOf(b as any);
            })
        : [];

    return (
        <AdminPanelContainer>
            <PanelHeader onClick={() => setIsPanelOpen(!isPanelOpen)}>
                <h2>Admin Panel</h2>
                <span>{isPanelOpen ? '▼' : '▶'}</span>
            </PanelHeader>

            <PanelContent isOpen={isPanelOpen}>
                <FetchControls>
                    <input type="text" value={fetchId} onChange={(e) => setFetchId(e.target.value)} placeholder="Enter AOTD ID to fetch/edit" />
                    <InfoButton onClick={handleFetchById} disabled={isLoading || !fetchId.trim()}> {isLoading && !aotdData ? 'Loading...' : 'Fetch by ID'} </InfoButton>
                    <InfoButton onClick={() => fetchData()} disabled={isLoading}> {isLoading && !aotdData ? 'Loading...' : "Load Today's AOTD"} </InfoButton>
                </FetchControls>

                {isLoading && <LoadingErrorDisplay>Loading data...</LoadingErrorDisplay>}
                {errorMsg && <LoadingErrorDisplay color="error">{errorMsg}</LoadingErrorDisplay>}

                {aotdData && editableData && (
                    <form onSubmit={handleSubmit}>
                        {editableData._id && renderField('_id', editableData)}
                        {editableData.__v !== undefined && renderField('__v', editableData)}

                        {fieldsToRender.map(key => renderField(key as keyof AOTDFormData, editableData))}
                        <ButtonGroup>
                            <PrimaryButton type="submit" disabled={isLoading}> {isLoading ? 'Saving...' : 'Save Changes'} </PrimaryButton>
                            <DangerButton type="button" onClick={handleDelete} disabled={isLoading || !aotdData?._id}> {isLoading ? 'Deleting...' : 'Delete This AOTD'} </DangerButton>
                        </ButtonGroup>
                    </form>
                )}
                {!isLoading && !aotdData && !errorMsg && <p>No AOTD data loaded. Fetch today's AOTD or by ID.</p>}
            </PanelContent>
        </AdminPanelContainer>
    );
};

export default ArtOfTheDayAdminPanel;
